import { useEffect, useMemo, useState } from "react";
import { Header } from "./components/Header";
import { MarketList } from "./components/MarketList";
import { MarketOverview } from "./components/MarketOverview";
import { PlatformMetrics } from "./components/PlatformMetrics";
import { TradePanel } from "./components/TradePanel";
import { useMarkets } from "./hooks/useMarkets";
import { usePlatformMetrics } from "./hooks/usePlatformMetrics";
import type { Market } from "./lib/types";

function initialMint(): string | undefined {
  return new URLSearchParams(window.location.search).get("token") || undefined;
}

export default function App() {
  const { markets, loading, refreshing, error, updatedAt, solUsdPrice, refresh } = useMarkets();
  const platform = usePlatformMetrics();
  const [selectedMint, setSelectedMint] = useState(initialMint);
  const selected = useMemo(
    () => markets.find((market) => market.mint === selectedMint) || markets[0],
    [markets, selectedMint],
  );
  useEffect(() => {
    if (!selected) return;
    if (selectedMint !== selected.mint) setSelectedMint(selected.mint);
    const url = new URL(window.location.href);
    url.searchParams.set("token", selected.mint);
    window.history.replaceState(null, "", url);
  }, [selected, selectedMint]);

  function selectMarket(market: Market) {
    setSelectedMint(market.mint);
    if (window.innerWidth < 980) {
      window.requestAnimationFrame(() =>
        document.querySelector(".market-summary")?.scrollIntoView({ behavior: "smooth", block: "start" }),
      );
    }
  }

  return (
    <div className="app-shell">
      <Header />
      <main>
        <section className="page-heading">
          <div>
            <h1>Trade Tribe tokens</h1>
            <p>Choose a token, connect your wallet, and trade directly on Solana.</p>
          </div>
          <div className="sync-row">
            <span className={error ? "sync-error" : ""}>
              <i /> {error ? "Using cached data" : updatedAt ? `Live · ${new Date(updatedAt).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}` : "Connecting…"}
            </span>
            <button onClick={() => void refresh()} disabled={refreshing}>{refreshing ? "Refreshing…" : "Refresh"}</button>
          </div>
        </section>

        <PlatformMetrics
          metrics={platform.metrics}
          solUsdPrice={solUsdPrice}
          loading={platform.loading}
          error={platform.error}
          onRefresh={() => void platform.refresh()}
        />

        <div className="trading-layout">
          <MarketList
            markets={markets}
            selectedMint={selected?.mint}
            onSelect={selectMarket}
            loading={loading}
            solUsdPrice={solUsdPrice}
          />
          <div className="market-workspace">
            {selected ? (
              <>
                <MarketOverview market={selected} solUsdPrice={solUsdPrice} />
                <TradePanel
                  market={selected}
                  onConfirmed={() => Promise.all([refresh(), platform.refresh()])}
                />
              </>
            ) : error && !loading ? (
              <section className="overview-card panel workspace-loading">
                <strong>Unable to load tokens</strong>
                <span>{error}</span>
                <button onClick={() => void refresh()}>Try again</button>
              </section>
            ) : (
              <section className="overview-card panel workspace-loading">
                <span className="spinner" />
                <strong>Loading tokens…</strong>
              </section>
            )}
          </div>
        </div>
      </main>
      <footer>
        <span>Independent community interface</span>
        <a href="https://solscan.io/account/B1x53qgNmAdZMfPVZvu89qDmNn3RpdKajRFXqCzE7UPU" target="_blank" rel="noreferrer">View program ↗</a>
      </footer>
    </div>
  );
}
